<template>
    <div class="py-2" :key="`render-` + renderKey">
        <!--
            Change-bank mode that is NOT the paystub payer-change flow: the borrower
            connected a bank and still owes the account choice. Say exactly that —
            they have not "failed" anything, and telling them to reconnect would ask
            them to redo the step they already finished.
        -->
        <div v-if="showSelectionGuidance" data-testid="payment-account-selection-guidance" class="px-4 py-3">
            <div class="px-6 py-5 flex flex-col gap-4 text-center bg-white border border-primary-700 rounded-lg">
                <template v-if="accounts.length">
                    <p>
                        You connected a new bank, but you haven't selected a payment account from it.
                        Please select an account from your connected bank.
                    </p>
                </template>
                <template v-else>
                    <p>
                        We could not find a payment account in the bank you connected.
                        Please reconnect your bank and select the account where you receive your income.
                    </p>
                    <p>
                        <a href="/account/change-bank"
                           data-testid="payment-account-selection-reconnect"
                           class="text-blue-600 underline">
                            Reconnect your bank
                        </a>
                    </p>
                </template>
            </div>
        </div>

        <template v-if="accounts.length">
          
            <div v-if="Object.keys(errors).length && errors?.banking_account_id" class="px-4 py-3 relative">
              <div class="close absolute top-4 right-5 text-xl font-bold text-gray-500 hover:text-red-500 cursor-pointer" @click="$emit('clearErrors')">
                &times;
              </div>             
                <div class="px-4 py-3 flex flex-col gap-4 text-center bg-white border border-red-300 text-red-500 rounded-lg">
                    <p v-for="error in errors.banking_account_id ">
                      {{ error }}
                    </p>
                </div>
            </div>

            <div v-if="changeBank && resolvingPayerChange && !hasProcessingAccounts" class="px-4 py-3 relative">
              <div class="close absolute top-4 right-5 text-xl font-bold text-gray-500 hover:text-red-500 cursor-pointer" @click="$emit('hideChangeBankMsg')">
                &times;
              </div>
              <div class="px-6 py-5 flex flex-col gap-4 text-center bg-white border border-red-300 text-red-500 rounded-lg">
                <p>
                  We were unable to identify income in this account. Please select the bank account through which you receive your income.
                </p>
                <p>
                  <a href="/account/change-bank" class="text-blue-600 underline">
                    If you don't see your preferred bank account on the list, start again with Plaid.
                  </a>
                </p>
              </div>
            </div>
          
            <p v-if="resolvingPayerChange" class="px-4 py-1">Please select the account where the pay shown on your paystub was deposited.</p>
            <p v-else class="px-4 py-1">Please select the account through which you receive the majority of your income.</p>

            <div v-if="loadingAccountNumbers" class="px-4 py-3">
                <p class="px-4 text-center bg-white border border-red-300 text-red-500 p-3 rounded-lg">
                    Still working... just a moment!
                </p>
            </div>
            <div v-else-if="loadingAccountNumbersLong || edgeReportPollingTimedOut" class="px-4 py-3">
                <p class="px-4 text-center bg-white border border-red-300 text-red-500 p-3 rounded-lg">
                    It's taking longer than usual. We'll email you shortly.
                </p>
            </div>

            <div v-if="selectionErrorMessage" class="px-4 py-3">
                <div class="px-4 py-3 text-center bg-white border border-red-300 text-red-500 rounded-lg">
                    {{ selectionErrorMessage }}
                </div>
            </div>

            <div class="mb-4" data-testid="billing-account-list">
                <div v-for="account in accounts" :key="`account-${account.id}`"
                    :data-testid="`billing-account-${account.id}`"
                    @click="handleAccountClick(account)"
                    class="account-item mx-4 my-2.5 flex shadow items-center border py-4 px-5 rounded-lg"
                    :class="{
                        'cursor-pointer': isAccountSelectable(account),
                        'cursor-not-allowed opacity-60': !isAccountSelectable(account),
                        'border-primary bg-indigo-100': selected && selected.id === account.id && isAccountSelectable(account),
                        'bg-gray-50 border-gray-300': !selected || selected.id !== account.id || !isAccountSelectable(account)
                    }">
                    <div class="w-2/5 pr-5">
                        <p class="">Account</p>
                        <p class="mt-3">Account type</p>
                        <p class="mt-3" v-if="accountStatus(account) === 'income'">Income found:</p>
                        <p v-if="accountStatus(account) === 'processing'" class="text-sm mt-3">
                            {{ edgeReportPollingTimedOut ? "It's taking longer than usual. We'll email you shortly." : "We're processing your bank information. This usually takes a minute or two." }}
                        </p>
                    </div>
                    <div class="w-3/5 text-right" :class="{ 'font-semibold': selected && selected.id === account.id && isAccountSelectable(account) }">
                        <p class="truncate" v-text="account.official_name" />
                        <p class="mt-3">{{ formatSubType(account.subtype) }} ({{ account.mask }})</p>
                        <p v-if="accountStatus(account) === 'income'" class="text-sm text-success mt-3">
                            <i class="fas fa-check"></i> {{ account?.paystub_income_match ? 'Paystub deposit matched' : account?.income_description }}
                        </p>
                        <p v-else-if="accountStatus(account) === 'no_income'" class="text-sm text-red-500 mt-3">
                            <i class="fas fa-times"></i> {{ resolvingPayerChange ? 'Paystub deposit not found' : (isEdgeReportUnavailable(account) ? "We couldn't read this account. Please reconnect or pick another account." : 'No income found') }}
                        </p>
                        <div v-if="accountStatus(account) === 'processing' || account.isRefreshing" class="text-sm mt-3 flex justify-end">
                            <div class="flex items-center gap-2 justify-end">
                                <div v-show="(accountStatus(account) === 'processing' && !edgeReportPollingTimedOut) || account.isRefreshing" class="spinner">
                                    <div class="bubble"></div>
                                    <div class="bubble"></div>
                                    <div class="bubble"></div>
                                </div>
                                <a href=""
                                   :class="{ 'opacity-50 cursor-not-allowed pointer-events-none': account.isRefreshing }"
                                   @click.prevent.stop="!account.isRefreshing && checkIfEdgeReportReadyForBankingAccount(account, true)"
                                   class="text-sm text-blue-500 underline"
                                >
                                  Refresh...
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div class="flex justify-center my-2.5">
            <a v-if="accounts.length" :href="'/account/change-bank'" class="btn-change-bank no-underline text-gray-400">
                Change Bank
            </a>
            <a v-else :href="'/account/change-bank'" class="no-underline border border-red-400 bg-red-50 rounded text-red-400 text-center text-base font-semibold py-2.5 px-3">
                Re-connect Bank
            </a>
        </div>
    </div>
</template>

<script>
    import { forIn } from 'lodash';
    import { normalize } from '../../utils';
    export default {
        props: [
            'selected',
            'errors',
            'changeBank',
        ],
        data() {
            return {
                incomeInfoLoaded: false,
                renderKey: 0,
                isDisabledRefreshEdgeReport: false,
                selectionErrorMessage: null,
                edgeReportPollingInterval: null,
                edgeReportPollingStartedAt: null,
                edgeReportPollingTimedOut: false,
                edgeReportPollIntervalMs: 5000,
                edgeReportPollMaxDurationMs: 120000,
                isPollingEdgeReports: false,
                edgeData: [],
                // The loan id the server itself resolved for this borrower —
                // filled by the income-information response so the picker filters
                // against the same loan storeBilling() will act on.
                billingLoanId: null,
            };
        },

        /**
         * @returns {void}
         */
        async mounted() {
            this.selectOnlySelectableAccount();

            if (this.loadingAccountNumbers) {
                setTimeout(() => {
                    window.location = window.location;
                }, 5000);
            }

            await this.getIncomeInfo();

            if (this.allDepositoryAccounts.length) {
                await Promise.all(this.allDepositoryAccounts.map(account => this.checkIfEdgeReportReadyForBankingAccount(account)));
                await this.getIncomeInfo();
                this.selectOnlySelectableAccount();
                this.startEdgeReportPolling();
            }
        },

        beforeDestroy() {
            this.stopEdgeReportPolling();
        },

        computed: {
            resolvingPayerChange() {
                const loan = this.$root.user.loans[0];

                return loan?.status === 'income_verification'
                    && loan?.sub_status === 'paystubs_payer_change';
            },

            /**
             * @returns {Boolean}
             */
            loadingAccountNumbers() {
                return this.$root.user.loans[0].sub_status === 'gathering_account_numbers';
            },

            /**
             * @returns {Boolean}
             */
            loadingAccountNumbersLong() {
                return this.$root.user.loans[0].sub_status === 'gathering_account_numbers_long';
            },

            /**
             * The loan the SERVER acts on. `loans` is serialized unordered, so
             * loans[0] can be a different loan than the one storeBilling() and
             * the middleware use on a multi-loan borrower — and the change-bank
             * picker would then filter against the wrong banking user. The
             * income-information endpoint reports the loan it resolved
             * (activeOrLastLoan), so that id is authoritative once it arrives.
             * Until then fall back to the newest loan, which mirrors the server's
             * own latestLoan() fallback, rather than to whatever happened to be
             * serialized first.
             *
             * @returns {Object|undefined}
             */
            billingLoan() {
                const loans = this.$root.user.loans || [];

                if (this.billingLoanId) {
                    const resolved = loans.find(loan => loan.id === this.billingLoanId);

                    if (resolved) {
                        return resolved;
                    }
                }

                return loans.reduce((newest, loan) => (!newest || loan.id > newest.id ? loan : newest), null);
            },

            /**
             * Every depository account on the user, unfiltered. Lookups and the
             * per-account report/income fetches must use this list — the visible
             * `accounts` list below removes rows, and a $set against a removed
             * row would be lost.
             *
             * @returns {Array}
             */
            allDepositoryAccounts() {
                return _.flatten(_.map(this.$root.user.banking_users, 'banking_accounts'))
                    .filter(a => a.type === 'depository');
            },

            /**
             * The borrower connected a bank and only owes the account choice —
             * distinct from the paystub payer-change flow, which shares the
             * change-bank flag but means "we could not find your income here".
             *
             * @returns {Boolean}
             */
            showSelectionGuidance() {
                return !!this.changeBank && !this.resolvingPayerChange;
            },

            accounts() {
                const bankingAccounts = this.allDepositoryAccounts;

                if (!this.showSelectionGuidance || !this.billingLoan?.banking_user_id) {
                    return bankingAccounts;
                }

                // In change-bank mode the previous connection's accounts must not
                // be offered at all: picking one is what leaves a loan pointing at
                // a bank its Plaid report can never describe. The server re-checks
                // ownership AND report membership on save — that check is the
                // authority; this only keeps the borrower from being led into a
                // rejection.
                //
                // Report membership is the second filter and comes from the
                // server (in_current_bank_report on the income-information
                // response), because the browser cannot see the report's account
                // GUIDs. It only removes an account once the server has actually
                // answered for it: an unanswered account stays visible rather
                // than flickering out of a list the borrower is reading.
                return bankingAccounts.filter(account => {
                    if (account.banking_user_id !== this.billingLoan.banking_user_id) {
                        return false;
                    }

                    // report_pending keeps a fresh connect's accounts visible
                    // (as processing) while the asset report is still being
                    // generated — hiding them here would empty the picker and
                    // show the reconnect dead-end for a borrower who is merely
                    // early, not wrong.
                    return account.report_membership_received !== true
                        || account.in_current_bank_report === true
                        || account.report_pending === true;
                });
            },

            hasProcessingAccounts() {
                return this.accounts.some(account => this.accountStatus(account) === 'processing');
            }
        },

        methods: {
            /**
             * @param {Object} account
             * @returns {'processing'|'income'|'no_income'}
             */
            accountStatus(account) {
                // The connected bank's asset report is still being generated:
                // the account cannot be proven selectable yet (storeBilling
                // would answer "still reviewing"), so it shows as processing —
                // which also keeps hasProcessingAccounts true and the poller
                // running until the report lands and membership resolves.
                if (account?.report_pending === true) {
                    return 'processing';
                }

                if (this.resolvingPayerChange) {
                    if (account?.paystub_income_match === true) {
                        return 'income';
                    }

                    if (account?.edge_report_status_received !== true || account?.edge_report_processing === true) {
                        return 'processing';
                    }

                    return 'no_income';
                }

                if (this.isEdgeReportUnavailable(account)) {
                    return 'no_income';
                }

                if (account?.edge_report_exists === true && !!account?.income_description) {
                    return 'income';
                }

                if (account?.edge_report_exists === true && this.incomeInfoLoaded && !account?.income_description) {
                    return 'no_income';
                }

                if (account?.edge_report_processing === true) {
                    return 'processing';
                }

                if (account?.edge_report_status_received !== true) {
                    return 'processing';
                }

                return 'no_income';
            },

            /**
             * @param {Object} account
             * @returns {Boolean}
             */
            isEdgeReportFailed(account) {
                return account?.edge_report_failed === true || [
                    'generation_failed',
                    'ibv_data_acquire_failed',
                    'reconnect_required',
                ].includes(account?.edge_report_status);
            },

            /**
             * Plaid/Edge never handed us a usable report, or Edge explicitly failed.
             * Do not keep showing the processing state after the status endpoint resolves this.
             *
             * @param {Object} account
             * @returns {Boolean}
             */
            isEdgeReportUnavailable(account) {
                return this.isEdgeReportFailed(account) || (
                    account?.edge_report_status_received === true &&
                    account?.edge_report_exists !== true &&
                    account?.edge_report_processing !== true
                );
            },

            /**
             * An account is selectable only if its edge report is ready AND income was detected.
             *
             * @param {Object} account
             * @returns {Boolean}
             */
            isAccountSelectable(account) {
                return this.accountStatus(account) === 'income';
            },

            /**
             * Handle click on an account card. Blocks selection and shows an inline message
             * when the account is not selectable (edge report not ready or no income detected).
             *
             * @param {Object} account
             * @returns {void}
             */
            handleAccountClick(account) {
                if (this.isAccountSelectable(account)) {
                    this.selectionErrorMessage = null;
                    this.$emit('update:selected', account);
                    return;
                }

                if (this.accountStatus(account) === 'processing') {
                    this.selectionErrorMessage = "We're still processing this account — please wait a moment.";
                } else if (this.isEdgeReportUnavailable(account)) {
                    this.selectionErrorMessage = "We couldn't read this account. Please reconnect or pick another account.";
                } else {
                    this.selectionErrorMessage = this.resolvingPayerChange
                        ? 'We did not find the paystub deposit in this account. Please select the account where this income was deposited.'
                        : 'We did not find income in this account. Please select an account where you receive your income.';
                }
            },

            /**
             * @param {String} subType
             * @returns {String}
             */
            formatSubType(subType) {
                switch (subType) {
                    case 'cd':
                        return 'CD';
                    default:
                        return this.$options.filters.ucfirst(subType);
                }
            },

            async checkIfEdgeReportReadyForBankingAccount(bankingAccount, isRefreshing = false) {
                if (!bankingAccount?.id) {
                    return;
                }

                if (isRefreshing) {
                    this.$set(bankingAccount, 'isRefreshing', true);
                }

                try {
                    const response = await this.$http.get(`/account/income-information/${bankingAccount.id}`);

                    if (response.data) {
                        // Look the row up in the UNFILTERED list: the visible
                        // list can drop an account the moment this response
                        // arrives, and a $set against a dropped row is lost.
                        const account = this.allDepositoryAccounts.find(account => account.id === bankingAccount.id);

                        if (account) {
                            this.$set(account, 'edge_report_exists', response.data.edge_report_exists);
                            this.$set(account, 'edge_report_processing', response.data.edge_report_processing);
                            this.$set(account, 'edge_report_status', response.data.edge_report_status);
                            this.$set(account, 'edge_report_failed', response.data.edge_report_failed);
                            this.$set(account, 'paystub_income_match', response.data.paystub_income_match === true);
                            this.$set(account, 'edge_report_status_received', true);

                            // Report membership is the server's verdict on whether
                            // this account is actually in the connected bank's
                            // latest successful report — the same check storeBilling
                            // enforces, so the picker cannot lead the borrower into
                            // a rejection.
                            if (response.data.billing_loan_id) {
                                this.billingLoanId = response.data.billing_loan_id;
                            }

                            if (response.data.report_membership_received === true) {
                                this.$set(account, 'in_current_bank_report', response.data.in_current_bank_report === true);
                                this.$set(account, 'report_membership_received', true);
                            }

                            // Always mirrored, so an account leaves the pending
                            // state the moment the report lands (in either
                            // direction: proven present, or proven absent).
                            this.$set(account, 'report_pending', response.data.report_pending === true);

                            this.renderKey++;
                        }
                    }
                } catch (error) {
                    console.error('Unable to check Edge report status', error);
                }

                if (isRefreshing) {
                    await this.getIncomeInfo();
                    this.selectOnlySelectableAccount();
                    this.startEdgeReportPolling();
                    setTimeout(() => {
                        this.$set(bankingAccount, 'isRefreshing', false);
                    }, 5000);
                }
            },

            async getIncomeInfo() {
                if (!this.accounts?.length) {
                    return;
                }

                try {
                    const { data } = await this.$http.get(`/account/income-information`);
                    this.edgeData = data?.data || [];

                    //check if bank accounts have same account number last 4 digits
                    const last4DigitsNumbers = [];
                    this.accounts.forEach(account => last4DigitsNumbers.push(account.account_number_last_four));

                    const hasDuplicateLast4 = new Set(last4DigitsNumbers).size !== last4DigitsNumbers.length;

                    // An income the server already attached to a banking account (banking_account_id,
                    // resolved by the same rule the underwriting snapshot and the deposit step use)
                    // belongs to that account alone. Only an income the server could not place falls
                    // back to the last four of its Edge key, read without the "#2" twin suffix.
                    const edgeLastFour = (bankNumber) => {
                        const keyWithoutSuffix = String(bankNumber || '').replace(/#\d+$/, '');
                        const match = /(\d{4})$/.exec(keyWithoutSuffix);
                        return match ? match[1] : null;
                    };

                    for (const account of this.accounts) {
                        if (account.edge_report_exists === true) {
                            this.$set(account, 'income_description', null);
                            this.$set(account, 'edge_score', null);
                        }

                        let matchedById = false;

                        forIn(this.edgeData, (information) => {
                            if (information.banking_account_id != null) {
                                if (information.banking_account_id == account?.id) {
                                    this.$set(account, 'income_description', information.income_description);
                                    this.$set(account, 'edge_score', information.score);
                                    matchedById = true;
                                }
                                return;
                            }

                            if (matchedById) {
                                return;
                            }

                            let isSameUserBankWithEdge = information.banking_user_id == account?.banking_user_id;
                            let lastFour = edgeLastFour(information.bank_number);
                            let isSameAccountNumberWithEdge = lastFour !== null && lastFour == account?.account_number_last_four;

                            //if has same last 4 digits, then check if account name is the same because from edge we getting just last 4 digits
                            let isSameAccountNameWithEdge = true;
                            if(hasDuplicateLast4) {
                                isSameAccountNameWithEdge = normalize(account.name) == normalize(information.account_name);
                            }

                            if (isSameUserBankWithEdge && isSameAccountNumberWithEdge && isSameAccountNameWithEdge) {
                                this.$set(account, 'income_description', information.income_description);
                                this.$set(account, 'edge_score', information.score);
                            }
                        });
                    }

                    this.incomeInfoLoaded = true;
                } catch (error) {
                    console.error('Unable to get income information', error);
                }
            },

            selectOnlySelectableAccount() {
                if (this.accounts && this.accounts.length === 1 && this.isAccountSelectable(this.accounts[0])) {
                    this.$emit('update:selected', this.accounts[0]);
                }
            },

            startEdgeReportPolling() {
                if (this.edgeReportPollingInterval || !this.hasProcessingAccounts || this.edgeReportPollingTimedOut) {
                    return;
                }

                this.edgeReportPollingStartedAt = Date.now();
                this.edgeReportPollingInterval = setInterval(() => {
                    this.pollProcessingAccounts();
                }, this.edgeReportPollIntervalMs);
            },

            stopEdgeReportPolling() {
                if (!this.edgeReportPollingInterval) {
                    return;
                }

                clearInterval(this.edgeReportPollingInterval);
                this.edgeReportPollingInterval = null;
            },

            async pollProcessingAccounts() {
                if (this.isPollingEdgeReports) {
                    return;
                }

                if (!this.hasProcessingAccounts) {
                    this.stopEdgeReportPolling();
                    return;
                }

                if (this.edgeReportPollingStartedAt && Date.now() - this.edgeReportPollingStartedAt >= this.edgeReportPollMaxDurationMs) {
                    this.edgeReportPollingTimedOut = true;
                    this.stopEdgeReportPolling();
                    return;
                }

                this.isPollingEdgeReports = true;

                try {
                    const processingAccounts = this.accounts.filter(account => this.accountStatus(account) === 'processing');
                    await Promise.all(processingAccounts.map(account => this.checkIfEdgeReportReadyForBankingAccount(account)));
                    await this.getIncomeInfo();
                    this.selectOnlySelectableAccount();

                    if (!this.hasProcessingAccounts) {
                        this.stopEdgeReportPolling();
                    }
                } finally {
                    this.isPollingEdgeReports = false;
                }
            }
        }
    }
</script>
<style scoped>
.spinner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner .bubble {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #CED5E1;
  animation: bubble-animation 1.4s infinite ease-in-out both;
}

.spinner .bubble:nth-child(1) {
  animation-delay: -0.32s;
}

.spinner .bubble:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bubble-animation {
  0%, 80%, 100% { 
    transform: scale(0);
    opacity: 0.3;
  }
  40% { 
    transform: scale(1);
    opacity: 1;
  }
}
</style>